// Procedural arcade audio: three level-specific chiptunes and a compact player-hit SFX.
// Everything is synthesized through one AudioContext, so native builds remain fully offline.

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD = 0.12;
const STEPS_PER_BAR = 16;
const MUSIC_VOLUME = 0.18;
const SFX_VOLUME = 0.28;

export type MusicTrackKey = 'baseLab' | 'verdantStill' | 'starfallConduit' | 'ninjaKid';

type Chord = readonly [root: number, third: number];

export interface MusicTrack {
  readonly bpm: number;
  readonly progression: ReadonlyArray<Chord>;
  /** Triad indices for each sixteenth note. -1 leaves intentional space. */
  readonly arp: ReadonlyArray<number>;
  readonly leadWave: OscillatorType;
  readonly leadOctave: number;
  readonly leadGain: number;
  readonly leadGate: number;
  readonly bassWave: OscillatorType;
  readonly bassOctave: number;
  readonly bassGain: number;
  readonly bassEvery: number;
  readonly bassAccentEvery: number;
  readonly kickSteps: ReadonlyArray<number>;
  readonly snareSteps: ReadonlyArray<number>;
  readonly hatEvery: number;
  readonly hatGain: number;
  readonly hatFrequency: number;
  readonly sparkleSteps: ReadonlyArray<number>;
  readonly filterFrequency: number;
}

/** Distinct arrangements rather than pitch-shifted copies of one loop. */
export const MUSIC_TRACKS: Readonly<Record<MusicTrackKey, MusicTrack>> = {
  baseLab: {
    bpm: 140,
    progression: [[57, 3], [53, 4], [60, 4], [55, 4], [57, 3], [53, 4], [55, 4], [52, 4]],
    arp: [0, 1, 2, 3, 2, 1, 0, 1, 2, 3, 2, 1, 0, 1, 2, 3],
    leadWave: 'square',
    leadOctave: 12,
    leadGain: 0.13,
    leadGate: 0.9,
    bassWave: 'triangle',
    bassOctave: -12,
    bassGain: 0.5,
    bassEvery: 2,
    bassAccentEvery: 4,
    kickSteps: [0, 8],
    snareSteps: [4, 12],
    hatEvery: 2,
    hatGain: 0.08,
    hatFrequency: 7000,
    sparkleSteps: [],
    filterFrequency: 5500,
  },
  verdantStill: {
    bpm: 112,
    progression: [[50, 3], [46, 4], [53, 4], [48, 4], [55, 3], [46, 4], [48, 4], [57, 4]],
    arp: [0, -1, 1, -1, 2, -1, 1, -1, 0, -1, 2, -1, 3, -1, 2, -1],
    leadWave: 'triangle',
    leadOctave: 12,
    leadGain: 0.18,
    leadGate: 1.65,
    bassWave: 'sine',
    bassOctave: -12,
    bassGain: 0.55,
    bassEvery: 4,
    bassAccentEvery: 8,
    kickSteps: [0, 10],
    snareSteps: [6, 14],
    hatEvery: 4,
    hatGain: 0.055,
    hatFrequency: 4800,
    sparkleSteps: [3, 11],
    filterFrequency: 4200,
  },
  starfallConduit: {
    bpm: 156,
    progression: [[49, 3], [45, 4], [52, 4], [47, 4], [49, 3], [50, 4], [47, 4], [56, 4]],
    arp: [0, 2, -1, 1, 3, -1, 2, 1, 0, -1, 3, 2, -1, 1, 2, 3],
    leadWave: 'sawtooth',
    leadOctave: 24,
    leadGain: 0.09,
    leadGate: 0.62,
    bassWave: 'square',
    bassOctave: -12,
    bassGain: 0.34,
    bassEvery: 2,
    bassAccentEvery: 4,
    kickSteps: [0, 3, 8, 11],
    snareSteps: [4, 12],
    hatEvery: 1,
    hatGain: 0.035,
    hatFrequency: 8500,
    sparkleSteps: [7, 15],
    filterFrequency: 6200,
  },
  // NinjaKid Dojo: sneaky-then-striking minor-pentatonic runs over a driving beat.
  ninjaKid: {
    bpm: 148,
    progression: [[45, 3], [50, 4], [48, 3], [52, 4], [45, 3], [53, 4], [50, 4], [47, 4]],
    arp: [0, -1, 2, 1, -1, 3, 2, -1, 0, 2, -1, 1, 3, -1, 2, 1],
    leadWave: 'square',
    leadOctave: 12,
    leadGain: 0.11,
    leadGate: 0.7,
    bassWave: 'triangle',
    bassOctave: -12,
    bassGain: 0.42,
    bassEvery: 2,
    bassAccentEvery: 8,
    kickSteps: [0, 6, 8, 14],
    snareSteps: [4, 12],
    hatEvery: 2,
    hatGain: 0.05,
    hatFrequency: 7600,
    sparkleSteps: [5, 13],
    filterFrequency: 5800,
  },
};

const midi = (n: number): number => 440 * Math.pow(2, (n - 69) / 12);

export class Music {
  private ctx: AudioContext | null = null;
  private musicBus: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicFilter: BiquadFilterNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private timer: number | null = null;
  private step = 0;
  private nextTime = 0;
  private lastHitTime = -Infinity;
  private playing = false;
  private muted = false;
  private trackKey: MusicTrackKey = 'baseLab';

  constructor() {
    try {
      this.muted = localStorage.getItem('bm_muted') === '1';
    } catch {
      /* private mode - ignore */
    }
  }

  get isMuted(): boolean {
    return this.muted;
  }

  get currentTrack(): MusicTrackKey {
    return this.trackKey;
  }

  /** Select and restart an arena's musical phrase. Already-scheduled notes fade naturally. */
  setTrack(trackKey: MusicTrackKey): void {
    this.trackKey = trackKey;
    this.step = 0;
    if (this.ctx !== null) this.nextTime = this.ctx.currentTime + 0.06;
    if (this.ctx !== null && this.musicFilter !== null) {
      this.musicFilter.frequency.setTargetAtTime(
        MUSIC_TRACKS[trackKey].filterFrequency,
        this.ctx.currentTime,
        0.04,
      );
    }
  }

  /** Start the loop. Call from a user gesture so the AudioContext is allowed to run. */
  start(): void {
    if (this.playing) {
      void this.ctx?.resume();
      return;
    }
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctor();
      const musicBus = ctx.createGain();
      const sfxBus = ctx.createGain();
      musicBus.gain.value = this.muted ? 0 : MUSIC_VOLUME;
      sfxBus.gain.value = this.muted ? 0 : SFX_VOLUME;

      const soften = ctx.createBiquadFilter();
      soften.type = 'lowpass';
      soften.frequency.value = MUSIC_TRACKS[this.trackKey].filterFrequency;
      musicBus.connect(soften);
      soften.connect(ctx.destination);
      sfxBus.connect(ctx.destination);

      this.ctx = ctx;
      this.musicBus = musicBus;
      this.sfxBus = sfxBus;
      this.musicFilter = soften;
      this.noiseBuf = this.makeNoise(ctx);
      this.step = 0;
      this.nextTime = ctx.currentTime + 0.1;
      this.playing = true;
      this.timer = window.setInterval(() => this.scheduler(), LOOKAHEAD_MS);
    } catch {
      /* no Web Audio - game runs silent */
    }
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  /** The existing audio preference silences both music and gameplay effects. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.ctx !== null) {
      this.musicBus?.gain.setTargetAtTime(muted ? 0 : MUSIC_VOLUME, this.ctx.currentTime, 0.05);
      this.sfxBus?.gain.setTargetAtTime(muted ? 0 : SFX_VOLUME, this.ctx.currentTime, 0.02);
    }
    try {
      localStorage.setItem('bm_muted', muted ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  /** A short descending chip impact with a soft noise transient. */
  playPlayerHit(): void {
    const ctx = this.ctx;
    const sfxBus = this.sfxBus;
    const noiseBuf = this.noiseBuf;
    if (ctx === null || sfxBus === null || noiseBuf === null || this.muted) return;
    const time = ctx.currentTime;
    if (time - this.lastHitTime < 0.12) return;
    this.lastHitTime = time;

    const impact = ctx.createOscillator();
    impact.type = 'square';
    impact.frequency.setValueAtTime(175, time);
    impact.frequency.exponentialRampToValueAtTime(68, time + 0.1);
    const impactGain = ctx.createGain();
    impactGain.gain.setValueAtTime(0.32, time);
    impactGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.11);
    impact.connect(impactGain);
    impactGain.connect(sfxBus);
    impact.start(time);
    impact.stop(time + 0.12);

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.value = 920;
    noiseFilter.Q.value = 0.8;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.2, time);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.065);
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(sfxBus);
    noise.start(time);
    noise.stop(time + 0.075);
  }

  /** Re-resume after a tab switch (Safari/iOS suspend the context on background). */
  resume(): void {
    if (this.playing) void this.ctx?.resume();
  }

  /** Suspend immediately when a native game session is paused. */
  pause(): void {
    if (this.playing) void this.ctx?.suspend();
  }

  private scheduler(): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    const track = MUSIC_TRACKS[this.trackKey];
    const stepDuration = 60 / track.bpm / 4;
    while (this.nextTime < ctx.currentTime + SCHEDULE_AHEAD) {
      this.scheduleStep(track, this.step, this.nextTime, stepDuration);
      this.nextTime += stepDuration;
      this.step = (this.step + 1) % (track.progression.length * STEPS_PER_BAR);
    }
  }

  private scheduleStep(track: MusicTrack, step: number, time: number, stepDuration: number): void {
    const bar = Math.floor(step / STEPS_PER_BAR);
    const localStep = step % STEPS_PER_BAR;
    const [root, third] = track.progression[bar];
    const triad = [root, root + third, root + 7, root + 12];

    if (localStep % track.bassEvery === 0) {
      const accent = localStep % track.bassAccentEvery === 0 ? 1 : 0.62;
      const duration = localStep % track.bassAccentEvery === 0 ? 3.5 : 1.4;
      this.tone(
        midi(root + track.bassOctave),
        time,
        stepDuration * duration,
        track.bassWave,
        track.bassGain * accent,
      );
    }

    const arpIndex = track.arp[localStep];
    if (arpIndex >= 0) {
      this.tone(
        midi(triad[arpIndex] + track.leadOctave),
        time,
        stepDuration * track.leadGate,
        track.leadWave,
        track.leadGain,
      );
    }

    if (track.sparkleSteps.includes(localStep)) {
      this.tone(midi(root + 31), time, stepDuration * 1.8, 'sine', 0.08);
    }
    if (track.kickSteps.includes(localStep)) this.kick(time);
    if (track.snareSteps.includes(localStep)) this.noiseHit(time, 0.12, 0.22, 1800);
    if (localStep % track.hatEvery === 0) {
      this.noiseHit(time, 0.03, track.hatGain, track.hatFrequency);
    }
  }

  private tone(freq: number, time: number, duration: number, type: OscillatorType, gain: number): void {
    const ctx = this.ctx;
    const musicBus = this.musicBus;
    if (ctx === null || musicBus === null) return;
    const oscillator = ctx.createOscillator();
    oscillator.type = type;
    oscillator.frequency.value = freq;
    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0, time);
    envelope.gain.linearRampToValueAtTime(gain, time + 0.005);
    envelope.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    oscillator.connect(envelope);
    envelope.connect(musicBus);
    oscillator.start(time);
    oscillator.stop(time + duration + 0.02);
  }

  private kick(time: number): void {
    const ctx = this.ctx;
    const musicBus = this.musicBus;
    if (ctx === null || musicBus === null) return;
    const oscillator = ctx.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(140, time);
    oscillator.frequency.exponentialRampToValueAtTime(45, time + 0.12);
    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0.6, time);
    envelope.gain.exponentialRampToValueAtTime(0.0001, time + 0.16);
    oscillator.connect(envelope);
    envelope.connect(musicBus);
    oscillator.start(time);
    oscillator.stop(time + 0.18);
  }

  private noiseHit(time: number, duration: number, gain: number, frequency: number): void {
    const ctx = this.ctx;
    const musicBus = this.musicBus;
    const buffer = this.noiseBuf;
    if (ctx === null || musicBus === null || buffer === null) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = frequency;
    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(gain, time);
    envelope.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    source.connect(filter);
    filter.connect(envelope);
    envelope.connect(musicBus);
    source.start(time);
    source.stop(time + duration + 0.02);
  }

  private makeNoise(ctx: AudioContext): AudioBuffer {
    const length = Math.floor(ctx.sampleRate * 0.5);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }
}
